import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useParams, NavLink, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCircleCheck,
  FaCircle,
  FaClock,
  FaChevronDown,
  FaAward,
  FaLock,
  FaPenToSquare,
} from 'react-icons/fa6';
import CoursePlayerHeader from '../components/CoursePlayerHeader';
import { fetchJSON, postJSON, formatDuration } from '../lib/api';
import LessonBody from '../components/LessonBody';
import VideoPlayer from '../components/VideoPlayer';
import PodcastPlayer from '../components/PodcastPlayer';
import CourseReferences from '../components/CourseReferences';
import { useSelfCheckProgress } from '../lib/selfCheckProgress';
import { hasPassedFinal } from '../lib/finalQuizHistory';

export default function CourseLesson() {
  const { slug, moduleSlug, lessonSlug } = useParams();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const selfCheckProgress = useSelfCheckProgress(slug);
  // finalPassed: localStorage is the fast/offline signal; server is the
  // authoritative source for authenticated users (survives sign-out/device
  // switches and covers quiz attempts made before auth was available).
  const [serverFinalPassed, setServerFinalPassed] = useState(false);
  const finalPassed = serverFinalPassed || hasPassedFinal(slug);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    fetchJSON(`/api/quizzes/course/${slug}/final/history`)
      .then((data) => {
        if (alive && Array.isArray(data.attempts) && data.attempts.some((a) => a.passed)) {
          setServerFinalPassed(true);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug, user]);

  useEffect(() => {
    if (!course) return;
    const mod = course.modules?.find((m) => m.slug === moduleSlug);
    const lesson = mod?.lessons?.find((l) => l.slug === lessonSlug);
    document.title = lesson ? `${lesson.title} — Learning Outcomes Academy` : 'Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, [course, moduleSlug, lessonSlug]);

  // Server-persisted lesson completion — set of completed lesson IDs for this course.
  const [completedIds, setCompletedIds] = useState(new Set());
  const [markingComplete, setMarkingComplete] = useState(false);

  // Fetch completed lessons once auth and course are resolved.
  useEffect(() => {
    if (!user) { setCompletedIds(new Set()); return; }
    let alive = true;
    fetchJSON(`/api/progress/course/${slug}`)
      .then((data) => {
        if (alive) setCompletedIds(new Set(data.completedLessonIds || []));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug, user]);

  const toggleComplete = useCallback(async (lessonId) => {
    if (!user || markingComplete) return;
    const isNowComplete = !completedIds.has(lessonId);
    setMarkingComplete(true);
    // Optimistic update
    setCompletedIds((prev) => {
      const next = new Set(prev);
      isNowComplete ? next.add(lessonId) : next.delete(lessonId);
      return next;
    });
    try {
      const method = isNowComplete ? 'POST' : 'DELETE';
      await fetch(`/api/progress/lesson/${lessonId}/complete`, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      // Revert on failure
      setCompletedIds((prev) => {
        const next = new Set(prev);
        isNowComplete ? next.delete(lessonId) : next.add(lessonId);
        return next;
      });
    } finally {
      setMarkingComplete(false);
    }
  }, [user, markingComplete, completedIds]);

  // Inline Check-Your-Understanding questions for the lesson currently open.
  // Refetched whenever the lesson id changes; passed through to <LessonBody />
  // so each `{{check:slug}}` marker in body_md gets replaced by a question card.
  const [checks, setChecks] = useState([]);
  // Modules expanded in the sidebar ToC. Rules:
  //  - On Quick-Start (or before we know which module): keep Module 1 open as
  //    a teaser so the sidebar feels substantial.
  //  - Once the user is inside any other module: only that module is open;
  //    everything else collapses. Manual toggles add/remove temporarily but
  //    are reset on the next navigation.
  // Quick-Start itself is always rendered open (no chevron, single-item group).
  const initialOpen = () => {
    if (!moduleSlug || moduleSlug === 'quick-start') return new Set(['module-1']);
    return new Set([moduleSlug]);
  };
  const [openModules, setOpenModules] = useState(initialOpen);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchJSON(`/api/courses/${slug}`)
      .then((data) => { if (alive) { setCourse(data.course); setError(null); } })
      .catch((err) => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  // Resolve the lesson row from the route params before kicking off the
  // checks fetch — we need its DB id (not the URL slug) to hit the quizzes API.
  const lessonRowForChecks = useMemo(() => {
    if (!course) return null;
    for (const m of course.modules) {
      if (m.slug !== moduleSlug) continue;
      const found = m.lessons.find((l) => l.slug === lessonSlug);
      if (found) return found;
    }
    return null;
  }, [course, moduleSlug, lessonSlug]);
  const currentLessonId = lessonRowForChecks ? lessonRowForChecks.id : null;

  // Pull the inline Check-Your-Understanding questions for the open lesson.
  // Silent on failure — a missing or empty result simply means no inline
  // checks will render, which is the correct behaviour for lessons without any.
  useEffect(() => {
    if (!currentLessonId) {
      setChecks([]);
      return;
    }
    let alive = true;
    fetchJSON(`/api/quizzes/lesson/${currentLessonId}/checks`)
      .then((data) => { if (alive) setChecks(data.checks || []); })
      .catch(() => { if (alive) setChecks([]); });
    return () => { alive = false; };
  }, [currentLessonId]);

  // Whenever the user navigates to ANY lesson (via prev/next, the sidebar, or
  // direct URL), collapse all modules except the one they're now in. This
  // wipes out any temporary "I peeked at Module 5" expansions the user made
  // while reading earlier — keeping the ToC focused on where they are.
  // Quick-Start is a single-item header that's always rendered open; when the
  // user is on the Quick-Start lesson we also keep Module 1 open as a teaser.
  useEffect(() => {
    if (!moduleSlug) return;
    const focus = moduleSlug === 'quick-start' ? 'module-1' : moduleSlug;
    setOpenModules((prev) => {
      if (prev.size === 1 && prev.has(focus)) return prev; // no-op
      return new Set([focus]);
    });
  }, [moduleSlug, lessonSlug]);

  // Persist the current lesson path so "Go to course" in My Courses can
  // resume here. Skip Quick-Start — it's a free preview, not real progress.
  // For authenticated users also writes to the server so resume works across
  // devices.
  useEffect(() => {
    if (!slug || !moduleSlug || !lessonSlug) return;
    if (moduleSlug === 'quick-start') return;
    const path = `/courses/${slug}/${moduleSlug}/${lessonSlug}`;
    try {
      localStorage.setItem(`lo:last_lesson:${slug}`, path);
    } catch (_) {}
    if (user) {
      postJSON(`/api/progress/course/${slug}/resume`, { path }).catch(() => {});
    }
  }, [slug, moduleSlug, lessonSlug, user]);

  // Build flat lesson list for prev/next navigation
  const flatLessons = useMemo(() => {
    if (!course) return [];
    const out = [];
    course.modules.forEach((m) => {
      m.lessons.forEach((l) => out.push({ ...l, module: m }));
    });
    return out;
  }, [course]);

  // If auth has resolved and the user is signed out, show a two-option
  // interstitial: returning users can sign in, new visitors can buy.
  // Quick-Start is a free preview so it's always accessible.
  if (!authLoading && !loading && !user && moduleSlug !== 'quick-start') {
    const lockedLesson = course?.modules
      ?.flatMap((m) => m.lessons.map((l) => ({ ...l, module: m })))
      ?.find((l) => l.slug === lessonSlug);
    return (
      <div className="w-full bg-bone min-h-screen">
        <CoursePlayerHeader
          courseTitle={course?.title}
          courseHref={`/courses/${slug}`}
        />
        <div className="flex flex-col items-center justify-center min-h-[72vh] px-6 text-center">
          <p className="text-[11px] tracking-[0.2em] uppercase text-brand-deep mb-4">Full course access required</p>
          <h1 className="font-display text-3xl md:text-5xl text-ink mb-3 max-w-2xl leading-tight">
            {course?.title || 'Generative AI for Parents'}
          </h1>
          <p className="text-slate2 text-base mb-10">
            50 lessons · 6 modules · companion podcast · certificate of completion
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-lg">
            <Link
              to={`/login?next=${encodeURIComponent(location.pathname)}`}
              className="flex flex-col items-center gap-2 rounded-2xl border border-ink/15 bg-white px-6 py-8 hover:border-ink/30 hover:shadow-sm transition-all"
            >
              <span className="text-[10px] tracking-[0.2em] uppercase text-slate2">Already have access?</span>
              <span className="font-display text-2xl text-ink">Sign in</span>
            </Link>
            <Link
              to="/checkout"
              className="flex flex-col items-center gap-2 rounded-2xl bg-brand-deep px-6 py-8 hover:bg-brand-deep/90 transition-all"
            >
              <span className="text-[10px] tracking-[0.2em] uppercase text-bone/70">Get started today</span>
              <div className="flex items-baseline gap-2">
                <span className="text-bone/50 line-through text-base">CA$34.99</span>
                <span className="font-display text-2xl text-bone">CA$14.99</span>
              </div>
              <span className="text-[10px] tracking-[0.15em] uppercase text-bone/50">one-time · lifetime access</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading || authLoading) return <div className="container-page py-24"><div className="h-8 w-1/3 bg-ink/10 rounded animate-pulse" /></div>;
  if (error || !course) return (
    <div className="container-page py-24 text-center">
      <h1 className="font-display text-3xl text-ink">{error || 'Course not found'}</h1>
      <Link to="/courses" className="btn-link mt-6 inline-flex">Back to courses <FaArrowRight className="text-xs" /></Link>
    </div>
  );

  const currentIdx = flatLessons.findIndex(
    (l) => l.module.slug === moduleSlug && l.slug === lessonSlug
  );
  const lesson = currentIdx >= 0 ? flatLessons[currentIdx] : null;
  const prev = currentIdx > 0 ? flatLessons[currentIdx - 1] : null;
  const next = currentIdx >= 0 && currentIdx < flatLessons.length - 1 ? flatLessons[currentIdx + 1] : null;

  if (!lesson) return (
    <div className="container-page py-24 text-center">
      <h1 className="font-display text-3xl text-ink">Lesson not found</h1>
      <Link to={`/courses/${slug}`} className="btn-link mt-6 inline-flex">
        Back to course <FaArrowRight className="text-xs" />
      </Link>
    </div>
  );

  const lessonHref = (mSlug, lSlug) => `/courses/${slug}/${mSlug}/${lSlug}`;
  const cleanBody = (lesson.body_md || '').replace(/^_+|_+$/g, '').trim();
  // The References lesson sits inside Course Complete after the thank-you
  // and renders a custom React component (accordion + PDF download) instead
  // of the markdown body. The locked check below still applies the normal
  // way — when entitlement gating is wired, an unpaid viewer will see the
  // standard "Purchase the course to unlock" card here too — but the
  // placeholder branch is skipped because this lesson never needs a body.
  const isReferencesLesson =
    lesson.module.slug === 'course-complete' && lesson.slug === 'references';
  // Locked = paid lesson whose body the API didn't return because the viewer
  // hasn't purchased the course (real auth/gating wired in Step 5).
  const isLocked = !cleanBody && !lesson.is_preview;
  // Placeholder = lesson row exists in DB but has no body content yet AND
  // the viewer is allowed to see it (so it isn't actually locked). The
  // References lesson opts out because its content lives in a React
  // component rather than the markdown body.
  const isPlaceholder = !isReferencesLesson && !cleanBody && !isLocked;
  // Show real Stream / R2 players whenever the lesson row carries a
  // `video_id` or `audio_url` (set on Quick-Start, M1–M6 intros, and the
  // Course Complete lesson). The API only returns these fields when the
  // viewer is allowed to see them, so the gate happens server-side.
  const hasVideo = !!lesson.video_id;
  const hasPodcast = !!lesson.audio_url;

  // Footer prev/next metadata. When the adjacent lesson belongs to a different
  // module, the eyebrow becomes "Previous module" / "Next module" and the
  // visible label uses the module title — so users get a clear "you're moving
  // on" cue instead of a generic "Introduction" / "Resources" label.
  const navMeta = (target) => {
    if (!target) return null;
    const crosses = target.module.slug !== lesson.module.slug;
    return {
      to: lessonHref(target.module.slug, target.slug),
      eyebrow: crosses ? 'module' : null,
      label: crosses ? target.module.title : target.title,
    };
  };
  const prevMeta = navMeta(prev);
  const nextMeta = navMeta(next);

  return (
    <div className="w-full bg-bone min-h-screen">
      <CoursePlayerHeader
        courseTitle={course.title}
        courseHref={`/courses/${slug}`}
        progress={flatLessons.length > 0 ? `Lesson ${currentIdx + 1} of ${flatLessons.length}` : null}
      />

      <div className="container-page py-10 lg:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* MAIN ─────────────────────────────────────── */}
        <article className="lg:col-span-8 order-1">
          <div className="eyebrow eyebrow-bar">{lesson.module.title}</div>
          <h1 className="mt-4 font-display text-3xl lg:text-4xl leading-[1.1] tracking-tighter2 text-ink">
            {lesson.title}
          </h1>
          {lesson.duration_seconds > 0 && (
            <p className="mt-3 text-sm text-slate2 inline-flex items-center gap-2">
              <FaClock className="text-[10px]" /> {formatDuration(lesson.duration_seconds)}
            </p>
          )}

          {hasVideo && (
            <div id="welcome-video" className="mt-8 scroll-mt-24">
              <VideoPlayer lessonId={lesson.id} />
              {lesson.module.slug === 'quick-start' && (
                <p className="mt-2 text-xs text-slate2">
                  Dr. Q avatar &middot; AI-generated
                </p>
              )}
            </div>
          )}

          {hasPodcast && (
            <div className={hasVideo ? 'mt-6' : 'mt-8'}>
              <PodcastPlayer lessonId={lesson.id} />
              {lesson.module.slug === 'quick-start' && (
                <p className="mt-2 text-xs text-slate2">
                  Companion podcast &middot; AI-generated from the course content
                </p>
              )}
            </div>
          )}

          {/* Lesson body */}
          <div className="mt-10">
            {isLocked ? (
              <div className="mt-5 rounded-2xl border border-brand-deep/15 bg-brand-deep/5 p-8 text-center">
                <div className="font-display text-2xl text-brand-deep">
                  Purchase the course to unlock this lesson
                </div>
                <p className="mt-3 text-sm text-slate2 max-w-md mx-auto leading-relaxed">
                  This lesson is part of the paid course. Buy once and you'll have lifetime
                  access to all modules, written guides, and the companion podcast.
                </p>
                <Link
                  to={`/courses/${slug}`}
                  className="btn-primary mt-6 inline-flex"
                >
                  See pricing & buy course
                </Link>
                <p className="mt-3 text-xs text-slate2/80">
                  Want a taste first? Read the free{' '}
                  <Link
                    to={`/courses/${slug}/quick-start/guide`}
                    className="underline underline-offset-2 hover:text-ink"
                  >
                    Quick-Start Guide
                  </Link>
                  .
                </p>
              </div>
            ) : isPlaceholder ? (
              <div className="mt-5 rounded-2xl border border-dashed border-ink/15 bg-cream/60 p-8 text-center">
                <div className="font-display text-xl text-ink/80">Lesson content coming soon</div>
                <p className="mt-2 text-sm text-slate2 max-w-md mx-auto">
                  Written notes, key takeaways, and resources will appear here. The instructor can edit
                  this content from the admin panel.
                </p>
              </div>
            ) : (
              <div className="mt-5">
                {isReferencesLesson ? (
                  <CourseReferences />
                ) : (
                  <LessonBody md={cleanBody} checks={checks} courseSlug={slug} />
                )}
                {isReferencesLesson && (
                  // Soft-gate the certificate CTA on a passing final-quiz
                  // attempt. `finalPassed` is computed once at the top of
                  // the component and reused here and in the sidebar row,
                  // so a single localStorage read drives both affordances.
                  // Not a security gate — see hasPassedFinal() doc comment.
                  <div className="mt-12 rounded-2xl border border-brand-deep/20 bg-gradient-to-br from-brand-sage/30 to-brand-deep/5 p-8 sm:p-10 text-center">
                    <div className="text-[11px] tracking-[0.22em] uppercase text-brand-aubergine">
                      Your certificate
                    </div>
                    <div className="mt-3 font-display text-2xl sm:text-3xl text-brand-deep leading-tight">
                      {finalPassed
                        ? 'Generate \u0026 print your Certificate of Completion'
                        : 'One step left — pass the final assessment'}
                    </div>
                    <p className="mt-3 text-sm text-slate2 max-w-md mx-auto leading-relaxed">
                      {finalPassed
                        ? 'Print to paper or save as a PDF — yours to keep, share with your school community, or tape to the fridge.'
                        : 'Score 70% or higher on the 18-question assessment to unlock your Certificate of Completion.'}
                    </p>
                    <Link
                      to={finalPassed
                        ? `/courses/${slug}/certificate`
                        : `/courses/${slug}/final-quiz`}
                      className="btn-primary mt-6 inline-flex"
                    >
                      {finalPassed ? 'Open my certificate' : 'Go to the final assessment'}
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer nav */}
          <div className="mt-12 pt-6 border-t border-ink/10 space-y-4">
            {lesson.module.slug !== 'quick-start' && (() => {
              const isDone = completedIds.has(lesson.id);
              return (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => toggleComplete(lesson.id)}
                    disabled={!user || markingComplete}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDone
                        ? 'border-brand-deep bg-brand-deep text-bone hover:bg-brand-deep/80'
                        : 'border-ink/20 text-ink hover:bg-ink hover:text-bone'
                    }`}
                  >
                    <FaCircleCheck className={isDone ? 'text-bone' : 'text-ink/40'} />
                    {isDone ? 'Completed' : 'Mark complete'}
                  </button>
                </div>
              );
            })()}

            <div className="grid grid-cols-2 gap-4 items-center">
              {prevMeta ? (
                <Link
                  to={prevMeta.to}
                  className="flex items-center gap-2 text-sm text-slate2 hover:text-ink min-w-0"
                >
                  <FaArrowLeft className="text-[10px] flex-shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-[10px] tracking-[0.18em] uppercase text-slate2/70">
                      {prevMeta.eyebrow ? 'Previous module' : 'Previous'}
                    </span>
                    <span className="font-display text-base text-ink line-clamp-2">
                      {prevMeta.label}
                    </span>
                  </span>
                </Link>
              ) : <span />}

              {nextMeta ? (
                <Link
                  to={nextMeta.to}
                  className="flex items-center justify-end gap-2 text-sm text-slate2 hover:text-ink min-w-0 text-right"
                >
                  <span className="min-w-0">
                    <span className="block text-[10px] tracking-[0.18em] uppercase text-slate2/70">
                      {nextMeta.eyebrow ? 'Next module' : 'Next'}
                    </span>
                    <span className="font-display text-base text-ink line-clamp-2">
                      {nextMeta.label}
                    </span>
                  </span>
                  <FaArrowRight className="text-[10px] flex-shrink-0" />
                </Link>
              ) : <span />}
            </div>
          </div>
        </article>

        {/* SIDEBAR ─────────────────────────────────── */}
        <aside className="lg:col-span-4 order-2">
          <div className="lg:sticky lg:top-24">
            <div className="card-soft p-5">
              <div className="text-[11px] tracking-[0.18em] uppercase text-brand-deep mb-4">
                Course contents
              </div>
              <nav className="space-y-3 max-h-[70vh] overflow-y-auto overflow-x-hidden pr-1 toc-scroll">
                {course.modules.map((m) => {
                  // Quick-Start is a single-item group — always expanded, no
                  // chevron needed. Every other module is collapsible.
                  const isQuickStart = m.slug === 'quick-start';
                  const isOpen = isQuickStart || openModules.has(m.slug);
                  // The certificate is a separate route, not a lesson, but we
                  // surface it as a visually distinct row at the bottom of the
                  // Course Complete module so returning learners can find their
                  // certificate without scrolling to the end of the References
                  // lesson. It is intentionally NOT counted in any "lesson X of
                  // Y" math.
                  //
                  // FUTURE: gate this on "completed all six paid modules"
                  // (Modules 1–6). We don't have lesson-level progress tracking
                  // wired yet (Step 5 of the LMS roadmap), and this course has
                  // no quizzes or end-of-course test, so the gating mechanism
                  // is still TBD — likely "every lesson in Modules 1–6 has a
                  // matching `progress` row for the current user", possibly
                  // augmented by an explicit "Mark module complete" button.
                  // For now (review mode + entitlement gating not yet wired),
                  // always show it; the certificate page itself bounces
                  // unentitled visitors back to the course.
                  const showCertificateInSidebar = m.slug === 'course-complete';
                  const lessonsRendered = m.lessons.map((l) => {
                    const isCurrent = m.slug === moduleSlug && l.slug === lessonSlug;
                    return (
                      <li key={l.id}>
                        <NavLink
                          to={lessonHref(m.slug, l.slug)}
                          className={`flex items-start gap-2 text-xs leading-relaxed py-1.5 px-2 -mx-2 rounded-md transition-colors ${
                            isCurrent
                              ? 'bg-brand-deep/8 text-ink'
                              : 'text-slate2 hover:text-ink hover:bg-ink/5'
                          }`}
                        >
                          {completedIds.has(l.id)
                            ? <FaCircleCheck className="mt-0.5 text-[10px] flex-shrink-0 text-brand-deep" />
                            : <FaCircle className={`mt-1 text-[7px] flex-shrink-0 ${isCurrent ? 'text-brand-deep' : 'text-ink/20'}`} />
                          }
                          <span className="flex-1">{l.title}</span>
                          {l.duration_seconds > 0 && (
                            <span className="text-[10px] text-slate2/60 flex-shrink-0">
                              {Math.round(l.duration_seconds / 60)}m
                            </span>
                          )}
                        </NavLink>
                      </li>
                    );
                  });
                  return (
                    <div key={m.id}>
                      {isQuickStart ? (
                        <div className="mb-2 font-display text-sm text-ink leading-snug">
                          {m.title}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            // Accordion behavior: clicking a closed module opens
                            // it AND collapses every other module. Without this,
                            // expanding (say) Module 6 while Module 5 was already
                            // open pushes Module 6's lessons below the fold of
                            // the sidebar, forcing the user to scroll to see
                            // what they just opened. We also refuse to collapse
                            // the module the user is currently inside — keeping
                            // the active-lesson highlight visible at all times.
                            setOpenModules((prev) => {
                              if (prev.has(m.slug)) {
                                if (m.slug === lesson.module.slug) return prev;
                                return new Set();
                              }
                              return new Set([m.slug]);
                            })
                          }
                          aria-expanded={isOpen}
                          className="w-full flex items-start justify-between gap-2 mb-2 text-left group"
                        >
                          <span className="font-display text-sm text-ink leading-snug group-hover:text-brand-deep transition-colors">
                            {m.title}
                          </span>
                          <FaChevronDown
                            className={`text-[10px] text-slate2 mt-1.5 flex-shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                          />
                        </button>
                      )}
                      {isOpen && (
                        <ul className="ml-3 space-y-1 mb-1">
                          {lessonsRendered}
                          {showCertificateInSidebar && (
                            <>
                              {/* Final Assessment — visible-but-locked until
                                  all 48 self-checks are answered correctly.
                                  Locked rendering is a non-clickable row with
                                  a lock icon + "X of 48 complete" hint;
                                  unlocked renders as a normal NavLink that
                                  matches the Certificate row's style. */}
                              <li className="mt-1 pt-1.5 border-t border-ink/10">
                                {selfCheckProgress.allComplete ? (
                                  <NavLink
                                    to={`/courses/${slug}/final-quiz?from=${encodeURIComponent(lessonHref(moduleSlug, lessonSlug))}`}
                                    className={({ isActive }) =>
                                      `flex items-start gap-2 text-xs leading-relaxed py-1.5 px-2 -mx-2 rounded-md transition-colors ${
                                        isActive
                                          ? 'bg-brand-deep/8 text-ink'
                                          : 'text-slate2 hover:text-ink hover:bg-ink/5'
                                      }`
                                    }
                                  >
                                    <FaPenToSquare
                                      aria-hidden="true"
                                      className="mt-0.5 text-[11px] flex-shrink-0 text-brand-deep"
                                    />
                                    <span className="flex-1">Final Assessment</span>
                                  </NavLink>
                                ) : (
                                  <div
                                    className="flex items-start gap-2 text-xs leading-relaxed py-1.5 px-2 -mx-2 rounded-md text-slate2/60"
                                    aria-disabled="true"
                                    title={`Answer all ${selfCheckProgress.total} Check-Your-Understanding questions to unlock`}
                                  >
                                    <FaLock
                                      aria-hidden="true"
                                      className="mt-0.5 text-[11px] flex-shrink-0"
                                    />
                                    <span className="flex-1">
                                      Final Assessment
                                      {selfCheckProgress.total > 0 && (
                                        <span className="block text-[10px] text-slate2/60 mt-0.5">
                                          {selfCheckProgress.count}/{selfCheckProgress.total} self-checks
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                )}
                              </li>
                              <li>
                                {finalPassed ? (
                                  <NavLink
                                    to={`/courses/${slug}/certificate`}
                                    className={({ isActive }) =>
                                      `flex items-start gap-2 text-xs leading-relaxed py-1.5 px-2 -mx-2 rounded-md transition-colors ${
                                        isActive
                                          ? 'bg-brand-deep/8 text-ink'
                                          : 'text-slate2 hover:text-ink hover:bg-ink/5'
                                      }`
                                    }
                                  >
                                    <FaAward
                                      aria-hidden="true"
                                      className="mt-0.5 text-[11px] flex-shrink-0 text-brand-aubergine"
                                    />
                                    <span className="flex-1">Certificate of Completion</span>
                                  </NavLink>
                                ) : (
                                  <div
                                    className="flex items-start gap-2 text-xs leading-relaxed py-1.5 px-2 -mx-2 rounded-md text-slate2/60"
                                    aria-disabled="true"
                                    title="Pass the Final Assessment to unlock"
                                  >
                                    <FaLock
                                      aria-hidden="true"
                                      className="mt-0.5 text-[11px] flex-shrink-0"
                                    />
                                    <span className="flex-1">
                                      Certificate of Completion
                                      <span className="block text-[10px] text-slate2/60 mt-0.5">
                                        Pass the Final Assessment first
                                      </span>
                                    </span>
                                  </div>
                                )}
                              </li>
                            </>
                          )}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
